import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"
import dagre from "cytoscape-dagre"
import {
  BLOCK_HEADER,
  buildElements,
  indexGraph,
  memberPosition,
  templates,
} from "../helpers/template_graph_model"

cytoscape.use(dagre)

const FALLBACK_NODE_COLOR = "#9ca3af"

// Fitting a small graph to the canvas would blow it up far past its natural size (thick
// lines, huge labels); cap it slightly above 1:1.
const MAX_FIT_ZOOM = 1.3
const FIT_PADDING = 40
const SEARCH_DEBOUNCE_MS = 200

// "Nach Vorlage" reads left to right from the templates to the employees; "Nach Mitarbeiter"
// mirrors it so the employees come first. Same nodes and edges, two reading directions.
// No animation: every filter change rebuilds the graph, and an animated dagre run per
// keystroke is what made large tenants stutter.
const LAYOUTS = {
  templates: { name: "dagre", rankDir: "LR", nodeSep: 12, rankSep: 120, animate: false },
  employees: { name: "dagre", rankDir: "RL", nodeSep: 12, rankSep: 120, animate: false },
}

// Vorlagen-Übersicht graph: Vorlage -> Gewerk / Rolle / Mitarbeitertyp -> Mitarbeitergruppe, built by
// helpers/template_graph_model.js from the JSON of Timesheet::TemplateGraphBuilder. Edge kinds:
// default (solid), available (dashed), excluded (red); "structure" edges join a template to its
// trade/role. Employees are bundled into groups (a count node, or an expanded block of name
// chips); tapping a group opens or closes it.
export default class extends Controller {
  static targets = [
    "canvas",
    "layoutButton",
    "templateFilter",
    "availableToggle",
    "withoutToggle",
    "search",
    "loadError",
  ]
  static values = { url: String, typeColors: Object, labels: Object }

  // The controls are live before the JSON arrives: every action returns early until this.cy
  // exists, and a disconnect during the fetch aborts it so no graph lands on a detached canvas.
  async connect() {
    this.currentLayout = "templates"
    // Groups flipped away from their default state (see expandedKeys).
    this.toggled = new Set()
    this.loading = new AbortController()
    const graph = await this.loadGraph(this.loading.signal)
    if (this.loading.signal.aborted) return
    if (!graph) {
      if (this.hasLoadErrorTarget) this.loadErrorTarget.hidden = false
      return
    }

    this.index = indexGraph(graph)
    this.cy = cytoscape({
      container: this.canvasTarget,
      style: this.stylesheet(),
      minZoom: 0.05,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    })

    this.populateTemplateFilter()
    this.wireTaps()
    this.applyFilters()
    this.markActiveLayout()
  }

  disconnect() {
    this.loading?.abort()
    clearTimeout(this.searchTimer)
    this.cy?.destroy()
    this.cy = null
  }

  async loadGraph(signal) {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal,
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  setLayout(event) {
    this.currentLayout = event.params.layout
    this.markActiveLayout()
    if (this.cy) this.render()
  }

  markActiveLayout() {
    this.layoutButtonTargets.forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        button.dataset.templateGraphLayoutParam === this.currentLayout,
      )
    })
  }

  zoomIn() {
    if (!this.cy) return
    this.cy.zoom({ level: this.cy.zoom() * 1.3, renderedPosition: this.center() })
  }

  zoomOut() {
    if (!this.cy) return
    this.cy.zoom({ level: this.cy.zoom() / 1.3, renderedPosition: this.center() })
  }

  fit() {
    if (!this.cy) return
    this.fitTo(this.cy.elements())
  }

  expandAll() {
    this.setAllGroups(true)
  }

  collapseAll() {
    this.setAllGroups(false)
  }

  // Flipped against each group's own default, so "all open" and "all closed" hold whatever the
  // default of the current view is.
  setAllGroups(open) {
    if (!this.cy) return
    this.toggled = new Set([...this.groups.keys()].filter((key) => this.defaults.has(key) !== open))
    this.render()
  }

  center() {
    const box = this.canvasTarget.getBoundingClientRect()
    return { x: box.width / 2, y: box.height / 2 }
  }

  // Rebuilt on every connect: a Turbo snapshot keeps the options added last time, so only the
  // server-rendered "all templates" option survives. The restored selection is kept if it
  // still exists.
  populateTemplateFilter() {
    const select = this.templateFilterTarget
    const selected = select.value
    Array.from(select.options)
      .filter((option) => option.value !== "")
      .forEach((option) => option.remove())
    templates(this.index)
      .map((node) => ({ value: String(node.record_id), label: node.label }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((option) => select.append(new Option(option.label, option.value)))
    select.value = selected
    if (select.selectedIndex === -1) select.value = ""
  }

  // Typing re-renders only once the user pauses; the toggles and the select apply at once.
  search() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.applyFilters(), SEARCH_DEBOUNCE_MS)
  }

  // A filter change starts from the default state again: groups opened under another filter
  // have other members now, and keeping them open would reveal far more than was asked for.
  applyFilters() {
    if (!this.cy) return
    this.toggled.clear()
    this.render()
  }

  filters() {
    return {
      templateId: this.templateFilterTarget.value,
      showAvailable: this.availableToggleTarget.checked,
      onlyWithout: this.withoutToggleTarget.checked,
      query: this.searchTarget.value,
    }
  }

  render({ focus = null } = {}) {
    const { elements, groups, defaults } = buildElements(
      this.index,
      this.filters(),
      this.toggled,
      this.labelsValue,
    )
    this.groups = groups
    this.defaults = defaults
    this.cy.batch(() => {
      this.cy.elements().remove()
      this.cy.add(elements)
    })
    this.runLayout(focus)
  }

  // Dagre places everything but the chips: an expanded group is one node sized for its grid,
  // and its chips are laid into it afterwards.
  runLayout(focus) {
    const layout = this.cy
      .elements()
      .not('[type = "member"]')
      .layout({ ...LAYOUTS[this.currentLayout], fit: false })
    layout.one("layoutstop", () => {
      this.placeMembers()
      const target = focus && this.cy.getElementById(focus)
      this.fitTo(
        target?.nonempty()
          ? target.union(this.cy.nodes(`[group = "${focus}"]`))
          : this.cy.elements(),
      )
    })
    layout.run()
  }

  placeMembers() {
    this.cy.batch(() => {
      this.cy.nodes('[type = "group"][?expanded]').forEach((group) => {
        const size = { width: group.data("w"), height: group.data("h"), cols: group.data("cols") }
        const center = group.position()
        this.cy.nodes(`[type = "member"][group = "${group.id()}"]`).forEach((member) => {
          member.position(memberPosition(member.data("position"), size, center))
        })
      })
    })
  }

  fitTo(collection) {
    this.cy.fit(collection, FIT_PADDING)
    if (this.cy.zoom() <= MAX_FIT_ZOOM) return

    this.cy.zoom(MAX_FIT_ZOOM)
    this.cy.center(collection)
  }

  wireTaps() {
    this.cy.on("tap", "node", (event) => {
      const node = event.target
      if (node.data("type") !== "group") this.highlight(node)
      else if (!node.data("expanded") || this.onHeader(node, event.position)) this.toggleGroup(node)
    })
    this.cy.on("tap", (event) => {
      if (event.target === this.cy) this.cy.elements().removeClass("faded highlighted")
    })
  }

  // An open block closes from its header only: a click that misses a chip by a few pixels
  // must not fold away the hundreds of names the user is reading.
  onHeader(node, position) {
    return position.y <= node.boundingBox().y1 + BLOCK_HEADER
  }

  toggleGroup(node) {
    const key = node.data("group")
    if (this.toggled.has(key)) this.toggled.delete(key)
    else this.toggled.add(key)
    this.render({ focus: node.data("expanded") ? null : node.id() })
  }

  // Everything the tapped node connects to: downstream for a template / trade / role (the
  // groups that get it and their chips), upstream for an employee chip (every group the
  // employee is in, open or not, and where each comes from).
  highlight(node) {
    let chain
    if (node.data("type") === "member") {
      const employeeId = node.data("employee_id")
      const chips = this.cy.nodes(`[type = "member"][employee_id = ${employeeId}]`)
      // Collapsed groups have no chip to find; the model knows who is in them.
      const groups = [...this.groups.values()]
        .filter((group) => group.members.some((member) => member.record_id === employeeId))
        .map((group) => this.cy.getElementById(`group:${group.key}`))
      chain = chips.union(this.groupsWithChain(groups))
    } else {
      const downstream = node.closedNeighborhood().union(node.successors())
      const groupIds = new Set(downstream.nodes('[type = "group"]').map((group) => group.id()))
      chain = downstream.union(
        this.cy.nodes('[type = "member"]').filter((chip) => groupIds.has(chip.data("group"))),
      )
    }
    this.cy.elements().removeClass("faded highlighted")
    this.cy.elements().not(chain).addClass("faded")
    chain.addClass("highlighted")
  }

  // The groups, their incoming edges, their sources and — for a trade or role — the
  // structure edge and template that group stands for.
  groupsWithChain(groups) {
    let chain = this.cy.collection()
    groups.forEach((group) => {
      chain = chain.union(group)
      group.incomers("edge").forEach((edge) => {
        const source = edge.source()
        chain = chain.union(edge).union(source)
        source
          .incomers("edge")
          .filter((up) => up.data("template_id") === group.data("template_id"))
          .forEach((up) => (chain = chain.union(up).union(up.source())))
      })
    })
    return chain
  }

  stylesheet() {
    return [
      // Every node is a pill with its label inside (sizes from the model); chips and expanded
      // blocks override it below.
      {
        selector: "node",
        style: {
          shape: "round-rectangle",
          width: "data(w)",
          height: "data(h)",
          label: "data(label)",
          "font-size": 12,
          "text-valign": "center",
          "text-halign": "center",
          "text-wrap": "ellipsis",
          "text-max-width": (n) => n.data("w") - 14,
          "min-zoomed-font-size": 5,
          "border-width": 1,
        },
      },
      {
        selector: 'node[type = "template"]',
        style: {
          "background-color": this.typeColorsValue.template || FALLBACK_NODE_COLOR,
          "border-color": this.typeColorsValue.template || FALLBACK_NODE_COLOR,
          color: "#ffffff",
          "font-weight": "bold",
        },
      },
      // A restricted template only reaches its assignments: drawn hollow.
      {
        selector: 'node[type = "template"][?restricted]',
        style: { "background-color": "#ecfdf5", "border-width": 2, color: "#065f46" },
      },
      {
        selector: 'node[type = "trade"]',
        style: {
          "background-color": "#fffbeb",
          "border-color": this.typeColorsValue.trade || FALLBACK_NODE_COLOR,
          color: "#92400e",
        },
      },
      {
        selector: 'node[type = "role"]',
        style: {
          "background-color": "#eff6ff",
          "border-color": this.typeColorsValue.role || FALLBACK_NODE_COLOR,
          color: "#1e40af",
        },
      },
      {
        selector: 'node[type = "employee_type"]',
        style: {
          "background-color": "#f0fdfa",
          "border-color": this.typeColorsValue.employee_type || FALLBACK_NODE_COLOR,
          color: "#115e59",
        },
      },
      // A collapsed group: a pill with its count, tapped to open.
      {
        selector: 'node[type = "group"]',
        style: {
          "background-color": "#f1f5f9",
          "border-color": "#94a3b8",
          color: "#334155",
          "font-weight": 600,
        },
      },
      // An expanded group: a light frame, its label as a header, the chips laid inside.
      {
        selector: 'node[type = "group"][?expanded]',
        style: {
          "background-color": "#f8fafc",
          "border-color": "#cbd5e1",
          "border-style": "dashed",
          "text-valign": "top",
          "text-margin-y": 22,
          "z-index": 0,
        },
      },
      {
        selector: 'node[type = "group"][kind = "excluded"]',
        style: { "border-color": "#f87171", color: "#b91c1c" },
      },
      {
        selector: 'node[type = "group"][kind = "without"]',
        style: {
          "background-color": "#ffffff",
          "border-width": 2,
          "border-color": "#ef4444",
          color: "#b91c1c",
        },
      },
      {
        selector: 'node[type = "member"]',
        style: {
          "background-color": "#ffffff",
          "border-width": 1,
          "border-color": "#cbd5e1",
          color: "#374151",
          "font-size": 10,
          "font-weight": 400,
          "z-index": 10,
        },
      },
      {
        selector: 'node[type = "member"][?without_template]',
        style: { "border-width": 2, "border-color": "#ef4444", color: "#b91c1c" },
      },
      {
        selector: "edge",
        style: {
          width: 1.5,
          "line-color": "#cbd5e1",
          "curve-style": "bezier",
          "target-arrow-shape": "none",
          opacity: 0.8,
        },
      },
      // A group edge is as thick as the group is large, so the big flows stand out.
      { selector: "edge[width]", style: { width: "data(width)" } },
      // Same colours as the group graph; the default edge is only a shade darker so the
      // solid / dashed / red distinction carries the meaning.
      { selector: 'edge[kind = "default"]', style: { "line-color": "#94a3b8" } },
      { selector: 'edge[kind = "available"]', style: { "line-style": "dashed" } },
      { selector: 'edge[kind = "excluded"]', style: { "line-color": "#f87171" } },
      { selector: ".faded", style: { opacity: 0.12 } },
      { selector: "node.highlighted", style: { "border-width": 3, "border-color": "#149d71" } },
      { selector: "edge.highlighted", style: { "line-color": "#149d71", opacity: 1 } },
      { selector: 'edge.highlighted[kind = "excluded"]', style: { "line-color": "#ef4444" } },
    ]
  }
}
